import React, { forwardRef } from 'react';
import { Badge, Button, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';

interface ToolbarButtonProps {
  icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>;
  label: string;
  tooltip: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  compact?: boolean;
  badgeCount?: number;
  dataAttribute?: string;
  disabled?: boolean;
  disableTooltip?: boolean;
}

export const ToolbarButton = forwardRef<HTMLElement, ToolbarButtonProps>(
  (
    {
      icon: Icon,
      label,
      tooltip,
      onClick,
      compact,
      badgeCount = 0,
      dataAttribute,
      disabled = false,
      disableTooltip = false,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isVeryWideScreen = useMediaQuery(theme.breakpoints.up('xl'));

    // Always compact unless on very wide screens and compact is not forced
    const shouldShowCompact = compact !== false || !isVeryWideScreen;

    const actionButton = shouldShowCompact ? (
      <IconButton
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={onClick}
        disabled={disabled}
        color="primary"
        size="medium"
        data-action={dataAttribute}
        sx={{
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Badge badgeContent={badgeCount} color="error">
          <Icon fontSize="medium" />
        </Badge>
      </IconButton>
    ) : (
      <Button
        ref={ref as React.Ref<HTMLButtonElement>}
        startIcon={
          <Badge badgeContent={badgeCount} color="error">
            <Icon fontSize="small" />
          </Badge>
        }
        onClick={onClick}
        disabled={disabled}
        data-action={dataAttribute}
        sx={{
          textTransform: 'none',
          px: 2,
          py: 1,
          color: 'text.primary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {label}
      </Button>
    );

    return disableTooltip ? (
      actionButton
    ) : (
      <Tooltip title={tooltip} arrow>
        {actionButton}
      </Tooltip>
    );
  },
);

ToolbarButton.displayName = 'ToolbarButton';
